/**
 * PanelNodeBottomPanel -- Tabbed floating panel for Panel Source node.
 *
 * Contains 2 tabs:
 * - Refine: Feedback textarea and regenerate button
 * - References: Image variants grid with selection
 *
 * Follows floating panel pattern: nopan nodrag, inverse-zoom scaling,
 * spring animation (stiffness 400, damping 25).
 */

import { memo } from 'react'
import { useViewport } from 'reactflow'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../../../lib/utils'
import { getThumbnailUrl } from '../../../../api/useAssetUrl'
import type { HistoryEntry } from './types'

export type PanelBottomPanelTab = 'refine' | 'assets'

interface PanelNodeBottomPanelProps {
  isVisible: boolean
  activeTab: PanelBottomPanelTab
  onTabChange: (tab: PanelBottomPanelTab) => void
  projectId?: string
  hasImage: boolean
  // Refine tab props
  feedbackText: string
  onFeedbackTextChange: (text: string) => void
  onSubmit: () => void
  isRunning: boolean
  // References tab props
  history?: HistoryEntry[]
  pickedIndex?: number
  onSelectVariant?: (index: number) => void
  cacheKey?: string
}

const TABS: { id: PanelBottomPanelTab; label: string }[] = [
  { id: 'refine', label: 'Refine' },
  { id: 'assets', label: 'References' },
]

export const PanelNodeBottomPanel = memo(function PanelNodeBottomPanel({
  isVisible,
  activeTab,
  onTabChange,
  projectId,
  hasImage,
  // Refine
  feedbackText,
  onFeedbackTextChange,
  onSubmit,
  isRunning,
  // References
  history = [],
  pickedIndex = 0,
  onSelectVariant,
  cacheKey,
}: PanelNodeBottomPanelProps) {
  const { zoom } = useViewport()
  const scale = Math.min(Math.max(1 / zoom, 0.5), 2)

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale, x: '-50%' }}
          animate={{ opacity: 1, y: 0, scale, x: '-50%' }}
          exit={{ opacity: 0, y: -10, scale, x: '-50%' }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
            opacity: { duration: 0.15 },
          }}
          className="absolute z-50 nopan nodrag"
          style={{
            top: '100%',
            left: '50%',
            marginTop: 8,
            transformOrigin: 'top center',
            width: 'calc(100% + 120px)',
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-surface-1 border border-border rounded-xl shadow-2xl overflow-hidden">
            {/* Tab Bar */}
            <div className="flex gap-1 p-2 border-b border-border-subtle">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={cn(
                    'flex-1 px-3 py-1.5 text-xs font-medium rounded-full relative transition-all duration-200',
                    activeTab === tab.id
                      ? ''
                      : 'text-text-muted hover:text-foreground'
                  )}
                  style={
                    activeTab === tab.id
                      ? {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          color: '#ffffff',
                        }
                      : undefined
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content -- 220px fixed height */}
            <div className="h-[220px]">
              {/* Refine Tab */}
              {activeTab === 'refine' && (
                <div className="h-full flex flex-col">
                  {hasImage ? (
                    <>
                      <div className="p-3 flex-1">
                        <textarea
                          value={feedbackText}
                          onChange={(e) =>
                            onFeedbackTextChange(e.target.value)
                          }
                          placeholder="Describe the changes you'd like to make to this panel..."
                          maxLength={1000}
                          rows={2}
                          className={cn(
                            'w-full text-sm text-foreground bg-transparent',
                            'border-0 px-1 py-1',
                            'resize-none focus:outline-none',
                            'placeholder:text-text-muted nowheel',
                            'leading-relaxed'
                          )}
                          onKeyDown={(e) => {
                            if (
                              e.key === 'Enter' &&
                              (e.metaKey || e.ctrlKey) &&
                              feedbackText.trim()
                            ) {
                              e.preventDefault()
                              onSubmit()
                            }
                          }}
                        />
                      </div>
                      <div className="px-3 py-2 border-t border-border-subtle flex items-center justify-end">
                        <button
                          onClick={onSubmit}
                          disabled={isRunning || !feedbackText.trim()}
                          className={cn(
                            'h-8 px-4 flex items-center justify-center gap-2 rounded-full text-sm font-medium transition-all',
                            isRunning || !feedbackText.trim()
                              ? 'bg-surface-2 text-text-muted cursor-not-allowed'
                              : 'bg-foreground hover:bg-foreground/90 text-background'
                          )}
                        >
                          {isRunning ? (
                            <>
                              <svg
                                className="w-3.5 h-3.5 animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                              <span>Refining...</span>
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2.5}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"
                                />
                              </svg>
                              <span>Refine</span>
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <svg
                        className="w-8 h-8 text-text-muted/30 mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-xs text-text-muted">
                        No image to refine
                      </p>
                      <p className="text-xxs text-text-muted/60 mt-1">
                        Generate a panel image first
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* References Tab */}
              {activeTab === 'assets' && (
                <div className="h-full p-3 overflow-y-auto nowheel">
                  {history.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <svg
                        className="w-8 h-8 text-text-muted/30 mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                      <p className="text-xs text-text-muted">
                        No variants yet
                      </p>
                      <p className="text-xxs text-text-muted/60 mt-1">
                        Refine to create variants
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {[...history]
                        .reverse()
                        .map((entry, displayIdx) => {
                          const realIdx =
                            history.length - 1 - displayIdx
                          const isSelected = realIdx === pickedIndex
                          const imagePath = entry.result?.local_path
                          const thumbUrl =
                            imagePath && projectId
                              ? getThumbnailUrl(
                                  projectId,
                                  imagePath,
                                  80,
                                  cacheKey
                                )
                              : null

                          return (
                            <button
                              key={entry.result?.id || realIdx}
                              onClick={() =>
                                onSelectVariant?.(realIdx)
                              }
                              className={cn(
                                'rounded-lg border-2 transition-all relative group',
                                isSelected
                                  ? 'border-white p-0.5'
                                  : 'border-transparent hover:border-border'
                              )}
                            >
                              <div
                                className={cn(
                                  'rounded-md overflow-hidden',
                                  isSelected ? 'rounded-[4px]' : ''
                                )}
                              >
                                {thumbUrl ? (
                                  <img
                                    src={thumbUrl}
                                    alt={`v${realIdx + 1}`}
                                    className="h-20 w-auto block rounded-md"
                                  />
                                ) : (
                                  <div className="w-20 h-20 bg-surface-2 flex items-center justify-center">
                                    <svg
                                      className="w-4 h-4 text-text-muted/30"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1}
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16"
                                      />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <div className="absolute top-1.5 right-1.5">
                                  <span className="text-white text-sm">
                                    &#9733;
                                  </span>
                                </div>
                              )}
                              <div className="absolute bottom-1.5 left-1.5 text-xxs text-white bg-black/60 px-1 rounded">
                                v{realIdx + 1}
                              </div>
                            </button>
                          )
                        })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
})

PanelNodeBottomPanel.displayName = 'PanelNodeBottomPanel'
